import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import cn from '@/utils/cn';
import { TCILogo } from '../icons';

interface SPABrandLinkProps {
  // A router path inside the SPA, or a full-page href for a legacy mount.
  href?: string;
  to?: string;
}

// How often a hover brings the stars out. Keyboard focus always does, so the state is discoverable.
export const SPARKLE_CHANCE = 0.25;

// min-h keeps the logo a 24px target (WCAG 2.5.8) even though the mark itself is shorter.
const className = cn(
  'tw-group tw-relative tw-flex tw-min-h-[24px] tw-w-fit tw-items-center tw-no-underline',
  'tw-opacity-90 hover:tw-opacity-100',
);
const logoClass = cn(
  'tw-h-5 tw-w-auto tw-fill-spa-logo-fill tw-transition-[fill] tw-duration-300',
  'group-hover:tw-fill-spa-accent-text group-focus-visible:tw-fill-spa-accent-text',
);
const label = 'Sysadmin Home';

const twinkle = 'tw-animate-[spa-twinkle_0.7s_ease-in-out_2]';
const sparkleClass = cn(
  'tw-pointer-events-none tw-absolute tw-text-[0.6875rem] tw-leading-none tw-opacity-0',
  `group-focus-visible:${twinkle} motion-reduce:tw-animate-none`,
);

// Where each star sits around the 1.25rem-tall mark, its colour, and how far behind the first it plays.
const SPARKLES = [
  'tw-left-[-0.625rem] tw-top-[-0.5rem] tw-text-spa-warning',
  'tw-left-[0.875rem] tw-top-[-0.75rem] tw-text-spa-accent-text tw-[animation-delay:90ms]',
  'tw-left-[3.25rem] tw-top-[-0.25rem] tw-text-spa-warning tw-[animation-delay:180ms]',
  'tw-left-[1.875rem] tw-top-[1.25rem] tw-text-spa-info tw-[animation-delay:270ms]',
  'tw-left-[0.5rem] tw-top-[1.125rem] tw-text-spa-accent-text tw-[animation-delay:360ms]',
];
const lastSparkle = SPARKLES.length - 1;

// The logo at the top of a rail: the way home. Hovering tints the letters; now and then it also
// twinkles a few stars around the mark, which play out even if the cursor has moved on. The stars
// are decoration, so assistive tech never sees them.
const SPABrandLink = ({ href, to }: SPABrandLinkProps) => {
  const [sparkling, setSparkling] = useState(false);

  const maybeSparkle = () => {
    if (!sparkling && Math.random() < SPARKLE_CHANCE) setSparkling(true);
  };

  const logo = (
    <>
      <TCILogo className={logoClass} />
      {SPARKLES.map((position, index) => (
        <span
          key={position}
          aria-hidden="true"
          className={cn(sparkleClass, position, sparkling && twinkle)}
          onAnimationEnd={index === lastSparkle ? () => setSparkling(false) : undefined}
        >
          ✦
        </span>
      ))}
    </>
  );

  if (to) {
    return <Link aria-label={label} className={className} to={to} onMouseEnter={maybeSparkle}>{logo}</Link>;
  }
  return <a aria-label={label} className={className} href={href} onMouseEnter={maybeSparkle}>{logo}</a>;
};

export default SPABrandLink;
